import axios from 'axios';
import store from '../redux/store';
import { refreshAccessToken, logout } from '../redux/auth/authSlice';
import { jwtDecode } from 'jwt-decode';

// Create an instance of Axios to use throughout your app
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || "http://127.0.0.1:8000",
  withCredentials: true, // include cookies
});

// Variable to store the refresh token promise
let refreshTokenPromise = null;

// Function to handle token refresh
const handleTokenRefresh = async () => {
  try {
    const newAccessToken = await store.dispatch(refreshAccessToken()).unwrap();
    return newAccessToken;
  } catch (error) {
    await store.dispatch(logout());
    throw error;
  }
};

// Function to check if token is expired or will expire soon
const isTokenExpiredOrClose = (token) => {
  if (!token) return true;
  
  try {
    const decoded = jwtDecode(token);
    // If token will expire in the next 30 seconds, consider it expired
    const isExpired = decoded.exp < (Date.now() / 1000) + 30;
    if (isExpired) {
      console.log('Token expired or expiring soon, refreshing...');
    }
    return isExpired;
  } catch (error) {
    console.log('Error decoding token:', error);
    return true;
  }
};

// Add a request interceptor to add auth header and handle token refresh
apiClient.interceptors.request.use(
  async (config) => {
    // Get current state
    const { auth } = store.getState();
    const token = auth.accessToken || localStorage.getItem('accessToken');
    
    // If we have a token, add it to the request
    if (token) {
      // Check if token is expired
      if (isTokenExpiredOrClose(token)) {
        try {
          // If a refresh is already in progress, wait for it
          if (!refreshTokenPromise) {
            console.log('Starting token refresh');
            refreshTokenPromise = handleTokenRefresh();
          } else {
            console.log('Using existing token refresh promise');
          }
          
          const newToken = await refreshTokenPromise;
          console.log('Token refreshed successfully');
          
          // Clear the promise
          refreshTokenPromise = null;
          
          // Set the new token in the request
          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
          }
        } catch (error) {
          console.error('Failed to refresh token:', error);
          refreshTokenPromise = null;
        }
      } else {
        // Token is still valid, use it
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor to handle 401 errors (expired token)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error response is 401 and the request hasn't been retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('Received 401, attempting to refresh token');
      originalRequest._retry = true;

      try {
        // If a refresh is already in progress, wait for it
        if (!refreshTokenPromise) {
          refreshTokenPromise = handleTokenRefresh();
        }
        const newAccessToken = await refreshTokenPromise;
        
        // Clear the promise after it's done
        refreshTokenPromise = null;

        // Update the Authorization header of the original request
        if (newAccessToken) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed after 401:', refreshError);
        refreshTokenPromise = null;
        throw refreshError;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;